import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useDragRotate
 * Two motion modes:
 *   - DRAG: momentum + spring (lands on nearest 180° face)
 *   - FLIP: deterministic tween to currentAngle + 180° (always lands on opposite face)
 */
const useDragRotate = ({
  sensitivity = 0.55,
  friction = 0.985,
  springStrength = 0.012,
  minVelocity = 0.02,
  dragThreshold = 5,
  flipDuration = 900,
  maxVelocity = 18
} = {}) => {
  const [angle, setAngle] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const startXRef = useRef(0);
  const startAngleRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);
  const movedRef = useRef(false);
  const totalMovementRef = useRef(0);
  const rafRef = useRef(null);
  const angleRef = useRef(0);

  // Tween state for FLIP mode
  const tweenRef = useRef(null); // { from, to, start, duration }

  const stopMomentum = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    tweenRef.current = null;
    setIsAnimating(false);
  }, []);

  // Physics tick (used by drag-release momentum)
  const physicsTick = useCallback(() => {
    velocityRef.current *= friction;

    let force = 0;
    if (Math.abs(velocityRef.current) < 0.5) {
      const target = Math.round(angleRef.current / 180) * 180;
      const delta = target - angleRef.current;
      force = delta * springStrength;
    }

    const next = angleRef.current + velocityRef.current + force;
    angleRef.current = next;
    setAngle(next);

    const target = Math.round(angleRef.current / 180) * 180;
    const distFromRest = Math.abs(angleRef.current - target);
    const settled =
      Math.abs(velocityRef.current) < minVelocity && distFromRest < 0.3;

    if (settled) {
      angleRef.current = target;
      setAngle(target);
      rafRef.current = null;
      setIsAnimating(false);
      return;
    }
    rafRef.current = requestAnimationFrame(physicsTick);
  }, [friction, springStrength, minVelocity]);

  // Tween tick (used by flip)
  const tweenTick = useCallback(() => {
    const tw = tweenRef.current;
    if (!tw) return;
    const now = performance.now();
    const t = Math.min(1, (now - tw.start) / tw.duration);
    // easeOutBack-ish — nice 3D coin feel without overshoot at the very end
    const ease = 1 - Math.pow(1 - t, 3);
    const v = tw.from + (tw.to - tw.from) * ease;
    angleRef.current = v;
    setAngle(v);
    if (t >= 1) {
      angleRef.current = tw.to;
      setAngle(tw.to);
      tweenRef.current = null;
      rafRef.current = null;
      setIsAnimating(false);
      return;
    }
    rafRef.current = requestAnimationFrame(tweenTick);
  }, []);

  const startPhysicsLoop = useCallback(() => {
    if (rafRef.current) return;
    setIsAnimating(true);
    rafRef.current = requestAnimationFrame(physicsTick);
  }, [physicsTick]);

  const startTweenLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setIsAnimating(true);
    rafRef.current = requestAnimationFrame(tweenTick);
  }, [tweenTick]);

  const startDrag = useCallback((clientX) => {
    stopMomentum();
    setIsDragging(true);
    movedRef.current = false;
    totalMovementRef.current = 0;
    startXRef.current = clientX;
    lastXRef.current = clientX;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;
    startAngleRef.current = angleRef.current;
  }, [stopMomentum]);

  const moveDrag = useCallback((clientX) => {
    const dx = clientX - startXRef.current;
    totalMovementRef.current = Math.max(totalMovementRef.current, Math.abs(dx));
    if (totalMovementRef.current > dragThreshold) movedRef.current = true;
    const newAngle = startAngleRef.current + dx * sensitivity;

    const now = performance.now();
    const dt = Math.max(1, now - lastTimeRef.current);
    let v = ((clientX - lastXRef.current) * sensitivity) / dt * 16;
    // Clamp velocity so a fast swipe doesn't launch the coin into hyperspace
    if (v >  maxVelocity) v =  maxVelocity;
    if (v < -maxVelocity) v = -maxVelocity;
    velocityRef.current = v;
    lastXRef.current = clientX;
    lastTimeRef.current = now;

    angleRef.current = newAngle;
    setAngle(newAngle);
  }, [sensitivity, dragThreshold, maxVelocity]);

  const endDrag = useCallback(() => {
    setIsDragging(false);
    startPhysicsLoop();
  }, [startPhysicsLoop]);

  /**
   * Flip — always land on the opposite face.
   * @param {number} direction +1 or -1 (which way to rotate)
   * @param {number} extraSpins extra full spins before landing (default 0)
   */
  const flip = useCallback((direction = 1, extraSpins = 0) => {
    const sign = direction >= 0 ? 1 : -1;
    const from = angleRef.current;
    const to = from + sign * (180 + 360 * extraSpins);
    tweenRef.current = { from, to, start: performance.now(), duration: flipDuration };
    startTweenLoop();
  }, [flipDuration, startTweenLoop]);

  /**
   * Snap — tween back to the nearest 180° face without spinning further.
   * Returns true if a snap was performed (meaning angle wasn't already settled).
   */
  const snapToFace = useCallback(() => {
    const from = angleRef.current;
    const target = Math.round(from / 180) * 180;
    if (Math.abs(from - target) < 0.5) return false;
    tweenRef.current = { from, to: target, start: performance.now(), duration: flipDuration };
    startTweenLoop();
    return true;
  }, [flipDuration, startTweenLoop]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => moveDrag(e.clientX);
    const onUp = () => endDrag();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, moveDrag, endDrag]);

  useEffect(() => () => stopMomentum(), [stopMomentum]);

  const handlers = {
    onMouseDown: (e) => {
      e.preventDefault();
      startDrag(e.clientX);
    },
    onTouchStart: (e) => {
      if (e.touches[0]) startDrag(e.touches[0].clientX);
    },
    onTouchMove: (e) => {
      if (e.touches[0]) moveDrag(e.touches[0].clientX);
    },
    onTouchEnd: () => endDrag()
  };

  const reset = useCallback(() => {
    stopMomentum();
    angleRef.current = 0;
    setAngle(0);
  }, [stopMomentum]);

  const wasDragged = useCallback(() => movedRef.current, []);

  return { angle, isDragging, isAnimating, handlers, reset, setAngle, wasDragged, flip, snapToFace };
};

export default useDragRotate;
